<template>
  <div id="app">
    <!-- 管理後台頁面不顯示導航欄 -->
    <NavBar v-if="!isAdminPage" />
    <router-view v-slot="{ Component, route: currentRoute }">
      <Transition 
        v-if="currentRoute.meta?.transition && currentRoute.meta.transition !== 'none'"
        :name="currentRoute.meta.transition"
        mode="out-in"
      >
        <component :is="Component" :key="currentRoute.path" />
      </Transition>
      <component 
        v-else
        :is="Component" 
        :key="currentRoute.path" 
      />
    </router-view>
    <CookieConsent />
    <!-- 調試面板（只在開發環境或手機端顯示） -->
    <DebugPanel v-if="showDebugPanel" />
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import NavBar from './components/NavBar.vue'
import DebugPanel from './components/DebugPanel.vue'
import { trackVisitor } from './utils/visitor-tracking'
import { logger } from './utils/logger'
import { useMeta } from './composables/useMeta'
import { useJsonLd } from './composables/useJsonLd'
import { checkAndRecoverPendingInvestment } from './services/investment-guard'

const route = useRoute()

// 判斷是否為管理後台頁面
const isAdminPage = computed(() => {
  return route.path.startsWith('/admin')
})

// 判斷是否顯示調試面板（只在開發環境顯示）
const showDebugPanel = computed(() => {
  return import.meta.env.DEV // 只在開發環境顯示
})

// ⭐ 動態 Meta 標籤管理（僅非管理後台頁面）
if (!route.path.startsWith('/admin')) {
  useMeta()
  // ⭐ JSON-LD Schema 注入（GEO - Generative Engine Optimization）
  useJsonLd()
}

// 頁面載入時追蹤訪客（僅非管理後台頁面）
onMounted(async () => {
  if (!isAdminPage.value) {
    // ⭐ 投資保護：全局檢查並恢復待處理的投資
    // 確保即使用戶不訪問投資頁面，也能恢復待處理的投資
    try {
      const recovered = await checkAndRecoverPendingInvestment()
      if (recovered) {
        logger.log('✅ [全局] 待處理投資已恢復')
      }
    } catch (error) {
      logger.error('[全局] 檢查待處理投資失敗:', error)
      // 不阻止頁面載入
    }
    
    try {
      await trackVisitor()
      logger.log('✅ 訪客追蹤已初始化')
    } catch (error) {
      logger.error('訪客追蹤失敗:', error)
      // 不阻止頁面載入，靜默失敗
    }
  }
})
</script>

<style>
#app {
  font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  background: #0A0F1F;
  color: #E5E7EB;
  min-height: 100vh;
}
</style>

